/**
 * Utilitários visuais compartilhados — Cockpit Metas / Hoshin.
 */
import React from 'react';
import {
  GOAL_HEALTH_BADGE,
  GOAL_HEALTH_DOT,
  GOAL_HEALTH_LABELS,
  type GoalHealth,
} from '../../services/hoshinDashboardData';

export function HealthBadge({ health }: { health: GoalHealth }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${GOAL_HEALTH_BADGE[health]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${GOAL_HEALTH_DOT[health]}`} />
      {GOAL_HEALTH_LABELS[health]}
    </span>
  );
}

export function MetricCell({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  sub?: string;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const valueTone = tone === 'success'
    ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'warning'
      ? 'text-amber-600 dark:text-amber-400'
      : tone === 'danger'
        ? 'text-red-600 dark:text-red-400'
        : 'text-slate-800 dark:text-slate-100';

  return (
    <div className="min-w-0 border-r border-slate-200 px-2.5 py-1.5 last:border-r-0 dark:border-slate-700">
      <div className="text-[9px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-[15px] font-semibold tabular-nums leading-tight ${valueTone}`}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-[9px] text-slate-500">{sub}</div>}
    </div>
  );
}

export function ProbabilityBadge({ value, label }: { value: number; label: string }) {
  const tone = value >= 80 ? 'success' : value >= 60 ? 'warning' : 'danger';
  return (
    <div className="flex items-baseline gap-2">
      <span className={`text-[22px] font-bold tabular-nums leading-none ${
        tone === 'success' ? 'text-emerald-600' : tone === 'warning' ? 'text-amber-600' : 'text-red-600'
      }`}>
        {value}%
      </span>
      <span className="text-[10px] text-slate-500">{label}</span>
    </div>
  );
}

export function DenseTable({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`overflow-hidden rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 ${className}`}>
      <table className="w-full border-collapse text-left">{children}</table>
    </div>
  );
}

export function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`border-b border-slate-200 bg-slate-50 px-2 py-1.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 ${className}`}>
      {children}
    </th>
  );
}

export function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <td className={`border-b border-slate-100 px-2 py-1.5 text-[11px] text-slate-700 dark:border-slate-800 dark:text-slate-200 ${className}`}>
      {children}
    </td>
  );
}

export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-widest text-slate-500">
      {children}
    </div>
  );
}

export function formatHoshinDate(value: string | null | undefined): string {
  if (!value) return 'Sem prazo';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return 'Sem prazo';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

export function isHoshinDatePast(value: string | null | undefined): boolean {
  if (!value) return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return false;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

export type WorkTone = 'empty' | 'moving' | 'late' | 'done';

export function workTone(progress: number, actionCount: number, targetDate: string | null | undefined): WorkTone {
  if (progress >= 100 && actionCount > 0) return 'done';
  if (actionCount <= 0) return 'empty';
  if (isHoshinDatePast(targetDate)) return 'late';
  return 'moving';
}

const WORK_LABEL: Record<WorkTone, string> = {
  empty: 'Sem ação',
  moving: 'Em andamento',
  late: 'Prazo vencido',
  done: 'Ações concluídas',
};

const WORK_CLASS: Record<WorkTone, string> = {
  empty: 'text-slate-500',
  moving: 'text-slate-600 dark:text-slate-300',
  late: 'text-red-600',
  done: 'text-emerald-700 dark:text-emerald-400',
};

export function WorkStatus({ tone }: { tone: WorkTone }) {
  return (
    <span className={`text-[10px] font-medium ${WORK_CLASS[tone]}`}>
      {WORK_LABEL[tone]}
    </span>
  );
}

export function MiniProgress({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1 w-16 overflow-hidden rounded bg-slate-200 dark:bg-slate-700">
        <div
          className={`h-full rounded ${value >= 80 ? 'bg-emerald-500' : value >= 50 ? 'bg-blue-500' : value >= 25 ? 'bg-amber-500' : 'bg-red-500'}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="text-[10px] font-medium tabular-nums text-slate-600 dark:text-slate-300">{value}%</span>
    </div>
  );
}
